import React, { useState, useEffect, useRef } from 'react';
import { Phone, PhoneOff, Play, RefreshCw, Sparkles, ShieldCheck, ShieldAlert, Smile, Frown, Meh, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VoiceLayout } from './VoiceLayout';
import { VOICE_AGENTS, CALL_SCENARIOS, AI_CALLS } from '@/data/voiceAgents';
import { usePageTitle } from '@/lib/usePageTitle';
import { cn } from '@/lib/utils';

const STATUS_FLOW = ['Dialling', 'Connected', 'Verifying', 'Discussing Balance', 'Capturing Commitment', 'Completed'];

const SAMPLE_DEBTORS = [...new Map(AI_CALLS.map((c) => [c.debtorId, { id: c.debtorId, name: c.debtorName, segment: c.segment, phone: c.phoneMasked }])).values()];

export default function TestSimulator() {
  usePageTitle('Test Simulator');
  const [agent, setAgent] = useState(VOICE_AGENTS[0].id);
  const [debtor, setDebtor] = useState(SAMPLE_DEBTORS[0].id);
  const [scenario, setScenario] = useState(CALL_SCENARIOS[1].id);
  const [running, setRunning] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [statusIdx, setStatusIdx] = useState(0);
  const stoppedRef = useRef(false);

  const a = VOICE_AGENTS.find((x) => x.id === agent);
  const d = SAMPLE_DEBTORS.find((x) => x.id === debtor);
  const s = CALL_SCENARIOS.find((x) => x.id === scenario);
  const fullScript = AI_CALLS.find((c) => c.transcript === undefined ? false : true)?.transcript; // fallback
  const lines = (AI_CALLS.find((c) => c.outcome === s.outcome) || AI_CALLS[0]).transcript;

  const reset = () => {
    stoppedRef.current = true;
    setTranscript([]); setStatusIdx(0); setRunning(false);
  };

  const start = async () => {
    reset();
    setTimeout(() => { stoppedRef.current = false; }, 50);
    setRunning(true);
    for (let i = 0; i < lines.length; i++) {
      await new Promise((r) => setTimeout(r, 900));
      if (stoppedRef.current) return;
      setTranscript((prev) => [...prev, lines[i]]);
      setStatusIdx(Math.min(STATUS_FLOW.length - 1, Math.floor((i + 1) / Math.max(1, lines.length / STATUS_FLOW.length))));
    }
    await new Promise((r) => setTimeout(r, 600));
    if (stoppedRef.current) return;
    setStatusIdx(STATUS_FLOW.length - 1);
    setRunning(false);
  };

  // auto-reset when scenario changes
  useEffect(() => reset(), [agent, debtor, scenario]);

  return (
    <VoiceLayout
      title="Test call simulator"
      subtitle="Spin up a mock outbound conversation against any seeded debtor profile. Inspect transcript, sentiment, compliance posture and extracted intents in real time."
      actions={(<Button size="sm" variant="outline" onClick={start} disabled={running} data-testid="sim-generate"><RefreshCw className={cn('h-4 w-4 mr-1.5', running && 'animate-spin')} aria-hidden="true" />Generate new mock transcript</Button>)}
    >
      <div className="grid lg:grid-cols-12 gap-4">
        {/* Setup panel */}
        <section className="lg:col-span-4 surface-card p-4 space-y-4">
          <p className="text-[10px] uppercase tracking-widest text-primary">Setup</p>

          <Field label="AI voice agent">
            <select value={agent} onChange={(e) => setAgent(e.target.value)} className="select-base" data-testid="sim-agent">
              {VOICE_AGENTS.filter((v) => v.status === 'Active' || v.status === 'Pending Approval').map((v) => (
                <option key={v.id} value={v.id}>{v.name} — {v.type}</option>
              ))}
            </select>
          </Field>

          <Field label="Sample debtor">
            <select value={debtor} onChange={(e) => setDebtor(e.target.value)} className="select-base" data-testid="sim-debtor">
              {SAMPLE_DEBTORS.map((v) => (
                <option key={v.id} value={v.id}>{v.name} — {v.segment}</option>
              ))}
            </select>
          </Field>

          <Field label="Scenario">
            <select value={scenario} onChange={(e) => setScenario(e.target.value)} className="select-base" data-testid="sim-scenario">
              {CALL_SCENARIOS.map((v) => (
                <option key={v.id} value={v.id}>{v.label}</option>
              ))}
            </select>
          </Field>

          <div className="rounded-lg border border-border bg-muted/40 p-3 text-[11px] space-y-1">
            <p><span className="text-muted-foreground">Voice:</span> {a.voice}</p>
            <p><span className="text-muted-foreground">Dialect:</span> {a.dialect}</p>
            <p><span className="text-muted-foreground">Tone:</span> {a.tone}</p>
            <p><span className="text-muted-foreground">Phone (masked):</span> <span className="font-mono-num">{d.phone}</span></p>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={start} disabled={running} className="flex-1 bg-success text-success-foreground hover:bg-success/90" data-testid="sim-start">
              <Phone className="h-4 w-4 mr-1.5" aria-hidden="true" />{running ? 'In progress…' : 'Start simulated call'}
            </Button>
            <Button onClick={reset} variant="outline" size="icon" aria-label="Stop and reset"><PhoneOff className="h-4 w-4" /></Button>
          </div>
        </section>

        {/* Transcript + state */}
        <section className="lg:col-span-5 surface-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest text-primary">Live transcript</p>
            <Badge variant="outline" className="text-[10px] gap-1.5">
              <span className={cn('h-1.5 w-1.5 rounded-full', running ? 'bg-success animate-pulse' : 'bg-muted-foreground/40')} aria-hidden="true" />
              {running ? 'In progress' : transcript.length ? 'Completed' : 'Idle'}
            </Badge>
          </div>

          {/* Status flow */}
          <ol className="flex items-center gap-1 text-[10px]" aria-label="Call status timeline">
            {STATUS_FLOW.map((st, i) => (
              <li key={st} className={cn('flex-1 px-2 py-1 rounded text-center border',
                i < statusIdx ? 'border-success/40 bg-success/10 text-success' :
                i === statusIdx ? 'border-primary/40 bg-primary/10 text-primary' :
                'border-border text-muted-foreground'
              )}>{st}</li>
            ))}
          </ol>

          <div className="h-80 overflow-y-auto scrollbar-thin space-y-2 rounded-lg border border-border bg-muted/30 p-3" dir="rtl">
            {transcript.length === 0 && <p className="text-[12px] text-muted-foreground" dir="ltr">Press <span className="font-medium text-foreground">Start</span> to begin a mock conversation.</p>}
            {transcript.map((line, idx) => (
              <div key={idx} className={cn('flex', line.who === 'agent' ? 'justify-start' : 'justify-end')}>
                <div className={cn('max-w-[80%] rounded-2xl px-3 py-1.5 text-[12.5px] leading-relaxed',
                  line.who === 'agent' ? 'bg-primary/10 text-foreground' : 'bg-onyx text-background')}>
                  <span className="text-[9px] uppercase tracking-widest opacity-60 block mb-0.5" dir="ltr">{line.who === 'agent' ? a.name : d.name}</span>
                  {line.ar}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Extracted fields */}
        <section className="lg:col-span-3 space-y-3">
          <ExtractTile label="Sentiment" value={s.sentiment} icon={sentimentIcon(s.sentiment)} tone={sentimentTone(s.sentiment)} />
          <ExtractTile label="Compliance" value="Passed" icon={ShieldCheck} tone="success" hint="All guardrails respected" />
          <ExtractTile label="Detected intent" value={s.outcome} />
          {s.ptp > 0 && <ExtractTile label="Promise-to-Pay" value={`SAR ${s.ptp.toLocaleString()}`} tone="success" />}
          {s.date    && <ExtractTile label="Payment date"   value={s.date} />}
          {s.outcome === 'Dispute Captured'  && <ExtractTile label="Dispute reason" value="Payment not reflected" tone="warning" />}
          {s.outcome === 'Hardship Captured' && <ExtractTile label="Hardship reason" value="Income reduced this month" tone="info" />}

          <div className="surface-card p-3 border-primary/30">
            <p className="text-[10px] uppercase tracking-widest text-primary mb-1.5 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" aria-hidden="true" />Recommended next action</p>
            <p className="text-[12px] leading-snug">{nextActionFor(s.outcome)}</p>
          </div>
        </section>
      </div>
    </VoiceLayout>
  );
}

const Field = ({ label, children }) => (
  <label className="block text-[11px] uppercase tracking-widest text-muted-foreground space-y-1">
    {label}
    {children}
  </label>
);

const ExtractTile = ({ label, value, icon: Icon, tone = 'default', hint }) => (
  <div className={cn('surface-card p-3',
    tone === 'success' && 'border-success/40 bg-success/5',
    tone === 'warning' && 'border-warning/40 bg-warning/5',
    tone === 'info'    && 'border-info/40    bg-info/5',
    tone === 'danger'  && 'border-destructive/40 bg-destructive/5',
  )}>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">{Icon && <Icon className="h-3 w-3" aria-hidden="true" />}{label}</p>
    <p className="text-sm font-medium mt-0.5">{value}</p>
    {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
  </div>
);

const sentimentIcon = (s) => s === 'Positive' ? Smile : s === 'Angry' ? Frown : s === 'Stressed' ? AlertTriangle : Meh;
const sentimentTone = (s) => s === 'Positive' ? 'success' : s === 'Angry' ? 'danger' : s === 'Stressed' ? 'warning' : 'default';

function nextActionFor(outcome) {
  switch (outcome) {
    case 'Paid':              return 'Auto-close case on SADAD settlement clearance.';
    case 'Plan Created':      return 'Send SADAD authorisation link via WhatsApp · arm plan-watcher.';
    case 'Promise to Pay':    return 'SMS confirmation + arm broken-promise watcher for ' + (Math.random() > 0.5 ? '+1 day' : '+3 days') + ' post-due.';
    case 'Hardship Captured': return 'Route to Supervisor — Hardship Program intake.';
    case 'Dispute Captured':  return 'Route to Legal Officer — hold collection 7 business days.';
    case 'Wrong Party':       return 'Mark phone for skip-trace verification.';
    default:                  return 'Mark DNC + suspend outreach.';
  }
}
